/**
 * scannerInputMixin.js
 *
 * Vue 2 Mixin for managing scanner input across Zebra TC200 (Android 9) and TC22 (Android 13) devices.
 *
 * HOW IT WORKS:
 * =============
 * Both TC200 and TC22 use Zebra DataWedge configured in "Keystroke Output" mode with Enter suffix.
 * When a barcode is scanned, DataWedge injects the entire scan data followed by Enter key into
 * the currently focused input element. This mixin manages a hidden input field that:
 *
 * 1. Stays focused unless user interacts with another text input
 * 2. Captures complete scan via @input event (no character-by-character buffering needed!)
 * 3. Auto-refocuses after idle period to ensure scans always work
 * 4. Doesn't interfere with UI interactions (scrolling, clicking, typing)
 *
 * WHY THIS APPROACH:
 * ==================
 * - DataWedge with Enter suffix sends complete scan atomically (no timing issues)
 * - Both devices behave identically (no device-specific code needed)
 * - Simpler than keyboard event listeners + buffers
 * - More reliable during UI interactions
 * - No inter-character delay configuration needed in DataWedge
 *
 * DATAWEDGE CONFIG REQUIRED:
 * ==========================
 * Profile: [Your App Package]
 * ├─ Keystroke Output: ENABLED
 * ├─ Action Key Character: ENTER (0x0D)
 * ├─ Multi Byte Character Support: ENABLED
 * └─ Inter-character Delay: 0ms (not needed!)
 *
 * USAGE:
 * ======
 * import scannerInputMixin from '@/mixins/scannerInputMixin';
 *
 * export default {
 *   mixins: [scannerInputMixin],
 *   data() {
 *     return {
 *       scannerOptions: {
 *         enabled: true,
 *         refocusDelay: 3000,
 *         debug: false
 *       }
 *     };
 *   },
 *   methods: {
 *     onScanReceived(scannedValue) {
 *       console.log('Scanned:', scannedValue);
 *       // Process scan...
 *     }
 *   }
 * };
 *
 * // In template:
 * <input ref="scannerInput" type="text" inputmode="none" class="scanner-input-hidden" />
 */

export default {
  data() {
    return {
      // Scanner state
      scannerIsScanning: false,
      scannerIsPaused: false,
      scannerLastInteraction: Date.now(),

      // Default options (can be overridden in component)
      scannerOptions: {
        enabled: true,
        refocusDelay: 3000,
        debug: false
      },

      // Character buffering for TC200 (sends char-by-char)
      scannerCharBuffer: '',
      scannerBufferTimeout: null,
      SCANNER_BUFFER_DELAY: 300, // ms to wait before processing buffer (increased for tablets)

      // Timers
      scannerRefocusTimer: null
    };
  },

  methods: {
    /**
     * Debug logger
     */
    scannerLog(...args) {
      if (this.scannerOptions.debug) {
        console.log('[ScannerInput]', ...args);
      }
    },

    /**
     * Handle scan input from DataWedge
     * DataWedge sends complete scan with Enter suffix, triggering input event once
     *
     * This method MUST be implemented in the component
     */
    onScanReceived(scannedValue) {
      console.warn('[ScannerInput] onScanReceived not implemented in component. Received:', scannedValue);
    },

    /**
     * Handle keydown for character-by-character scanning (TC200)
     */
    handleScannerKeydown(event) {
      if (!this.scannerOptions.enabled || this.scannerIsPaused) {
        return;
      }

      console.log('[ScannerInput] Keydown:', event.key, 'Buffer:', this.scannerCharBuffer, 'Buffer length:', this.scannerCharBuffer.length);

      // Handle Enter key - process the buffered scan
      if (event.key === 'Enter' || event.keyCode === 13) {
        event.preventDefault();
        if (this.scannerCharBuffer) {
          console.log('[ScannerInput] Enter detected, processing buffer:', this.scannerCharBuffer);
          this.processScanBuffer();
        }
        return;
      }

      // Handle printable characters (length === 1 means single character like 'a', 'K', ':', etc.)
      if (event.key && event.key.length === 1) {
        event.preventDefault();
        this.addToScanBuffer(event.key);
      }
    },

    /**
     * Add character to scan buffer (for TC200 char-by-char scanning)
     */
    addToScanBuffer(char) {
      // Clear any existing timeout
      if (this.scannerBufferTimeout) {
        clearTimeout(this.scannerBufferTimeout);
      }

      // Add character to buffer
      this.scannerCharBuffer += char;
      this.scannerIsScanning = true;
      this.scannerLog('Added to buffer:', char, '| Full buffer:', this.scannerCharBuffer);

      // Set timeout to process scan if no more input comes
      this.scannerBufferTimeout = setTimeout(() => {
        if (this.scannerCharBuffer) {
          this.scannerLog('Buffer timeout reached, processing:', this.scannerCharBuffer);
          this.processScanBuffer();
        }
      }, this.SCANNER_BUFFER_DELAY);
    },

    /**
     * Process the accumulated scan buffer
     */
    processScanBuffer() {
      // Clear timeout
      if (this.scannerBufferTimeout) {
        clearTimeout(this.scannerBufferTimeout);
        this.scannerBufferTimeout = null;
      }

      const cleanValue = this.scannerCharBuffer.trim();
      this.scannerLog('Processing scan buffer:', cleanValue, `(${cleanValue.length} chars)`);

      // Reset buffer
      this.scannerCharBuffer = '';

      // Skip empty values
      if (!cleanValue) {
        this.scannerIsScanning = false;
        return;
      }

      // Call component's scan handler
      try {
        this.onScanReceived(cleanValue);
      } catch (error) {
        console.error('[ScannerInput] Error in onScanReceived:', error);
      }

      // Ensure focus stays on input
      this.$nextTick(() => {
        if (this.$refs.scannerInput && document.activeElement !== this.$refs.scannerInput) {
          this.$refs.scannerInput.focus();
          this.scannerLog('Re-focused after scan');
        }
      });

      // Reset scanning state after brief delay
      setTimeout(() => {
        this.scannerIsScanning = false;
      }, 200);
    },

    /**
     * Handle scanner input event (for TC22 that sends complete string)
     */
    handleScannerInput(event) {
      if (!this.scannerOptions.enabled || this.scannerIsPaused) {
        console.log('[ScannerInput] Scanning disabled or paused, ignoring input');
        return;
      }

      const value = event.target.value;

      console.log('[ScannerInput] @input event received - Raw value:', value, `Length: ${value.length}`);

      // Skip empty values
      if (!value || !value.trim()) {
        event.target.value = '';
        console.log('[ScannerInput] Empty value, skipping');
        return;
      }

      // TC22 sends complete string at once
      const cleanValue = value.trim();
      console.log('[ScannerInput] @input event - Full scan received:', cleanValue, `(${cleanValue.length} chars)`);

      // Set scanning state
      this.scannerIsScanning = true;

      // Call component's scan handler
      try {
        this.onScanReceived(cleanValue);
      } catch (error) {
        console.error('[ScannerInput] Error in onScanReceived:', error);
      }

      // Clear input for next scan
      event.target.value = '';

      // Ensure focus stays on input
      this.$nextTick(() => {
        if (this.$refs.scannerInput && document.activeElement !== this.$refs.scannerInput) {
          this.$refs.scannerInput.focus();
          console.log('[ScannerInput] Re-focused after scan');
        }
      });

      // Reset scanning state after brief delay
      setTimeout(() => {
        this.scannerIsScanning = false;
      }, 200);
    },

    /**
     * Handle input focus
     */
    handleScannerFocus() {
      clearTimeout(this.scannerRefocusTimer);
      this.scannerLog('Input focused');
    },

    /**
     * Handle input blur - schedule refocus unless user is typing elsewhere
     */
    handleScannerBlur(event) {
      const relatedTarget = event.relatedTarget;

      // Check if user clicked on another input/textarea
      if (relatedTarget?.tagName === 'INPUT' || relatedTarget?.tagName === 'TEXTAREA') {
        this.scannerLog('User typing in another field, pausing auto-refocus');
        return;
      }

      // Check if user clicked on a button/select that might open a modal
      if (relatedTarget?.tagName === 'BUTTON' || relatedTarget?.tagName === 'SELECT') {
        this.scannerLog('User interacting with button/select, delaying refocus');
      }

      // Schedule refocus after idle period
      clearTimeout(this.scannerRefocusTimer);
      this.scannerRefocusTimer = setTimeout(() => {
        // Only refocus if:
        // 1. Input element still exists
        // 2. It's not already focused
        // 3. No other input is focused
        // 4. Scanning is not paused
        // 5. Scanning is enabled
        if (this.$refs.scannerInput &&
            document.activeElement !== this.$refs.scannerInput &&
            document.activeElement?.tagName !== 'INPUT' &&
            document.activeElement?.tagName !== 'TEXTAREA' &&
            !this.scannerIsPaused &&
            this.scannerOptions.enabled) {

          this.$refs.scannerInput.focus();
          this.scannerLog('Auto-refocused after idle period');
        }
      }, this.scannerOptions.refocusDelay);
    },

    /**
     * Track user interactions to prevent aggressive refocusing during active use
     */
    handleScannerUserInteraction(event) {
      this.scannerLastInteraction = Date.now();

      // Don't log every interaction unless debugging
      if (this.scannerOptions.debug && event.type === 'click') {
        this.scannerLog('User interaction:', event.type, event.target.tagName);
      }
    },

    /**
     * Pause scanning (e.g., during modal dialogs)
     */
    pauseScanning() {
      this.scannerIsPaused = true;
      this.scannerLog('Scanning paused');
    },

    /**
     * Resume scanning after pause
     */
    resumeScanning() {
      this.scannerIsPaused = false;
      this.scannerLog('Scanning resumed');

      // Refocus input
      this.$nextTick(() => {
        if (this.$refs.scannerInput) {
          this.$refs.scannerInput.focus();
        }
      });
    },

    /**
     * Initialize scanner input
     */
    initScannerInput() {
      this.scannerLog('Initializing scanner input');

      // Initial focus
      this.$nextTick(() => {
        if (this.$refs.scannerInput) {
          this.$refs.scannerInput.focus();
          this.scannerLog('Initial focus set');
        }
      });

      // Track user interactions globally
      window.addEventListener('click', this.handleScannerUserInteraction, { passive: true });
      window.addEventListener('touchstart', this.handleScannerUserInteraction, { passive: true });
      window.addEventListener('scroll', this.handleScannerUserInteraction, { passive: true, capture: true });

      this.scannerLog('Initialization complete');
    },

    /**
     * Cleanup scanner input
     */
    cleanupScannerInput() {
      this.scannerLog('Cleaning up scanner input');

      // Clear timers
      clearTimeout(this.scannerRefocusTimer);

      // Remove global listeners
      window.removeEventListener('click', this.handleScannerUserInteraction);
      window.removeEventListener('touchstart', this.handleScannerUserInteraction);
      window.removeEventListener('scroll', this.handleScannerUserInteraction, { capture: true });

      this.scannerLog('Cleanup complete');
    }
  },

  mounted() {
    // Only initialize if scanner input is enabled and ref exists
    if (this.scannerOptions.enabled && this.$refs.scannerInput) {
      this.initScannerInput();
    }
  },

  beforeDestroy() {
    this.cleanupScannerInput();
  }
};
